import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, useAuth } from "@/context/AuthContext";
import PageHead from "@/components/PageHead";
import {
  ArrowLeft, Download, RefreshCcw, Mail, ExternalLink, FileText, MailX,
} from "lucide-react";

const SOURCES = [
  { key: "all",        label: "All sources" },
  { key: "contact",    label: "Contact form" },
  { key: "site-audit", label: "Site audit" },
];

const WINDOWS = [
  { key: 7,   label: "7 days" },
  { key: 30,  label: "30 days" },
  { key: 90,  label: "90 days" },
];

const SOURCE_COLOR = {
  contact:      "hsl(var(--ink))",         // black
  "site-audit": "hsl(var(--brand-red))",   // red
};
const SOURCE_LABEL = {
  contact: "Contact",
  "site-audit": "Site audit",
};

const TIER_STYLE = {
  hot:  "bg-brand-red text-paper border-brand-red",
  warm: "text-ink border-ink",
  cold: "text-label border-[hsl(var(--line-strong))]",
};
const TIERS = ["all", "hot", "warm", "cold"];

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = (now - d.getTime()) / 1000;
    if (diff < 60)      return "just now";
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800)  return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function formatDay(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso; }
}

function csvEscape(v) {
  const s = String(v ?? "");
  return `"${s.replace(/"/g, '""')}"`;
}

// ---------- Chart (hand-rolled SVG, stacked bars) ----------
function LeadsChart({ stats }) {
  if (!stats || !stats.buckets?.length) return null;
  const buckets = stats.buckets;
  const maxTotal = Math.max(1, ...buckets.map((b) => b.total));
  const W = 100;             // viewBox width — scales fluidly
  const H = 32;              // viewBox height
  const barGap = 0.2;        // fraction of slot
  const slot = W / buckets.length;
  const barW = slot * (1 - barGap);

  return (
    <div className="w-full" data-testid="leads-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-40 md:h-52 block"
        role="img"
        aria-label={`Daily leads over the last ${stats.days} days`}
      >
        {/* baseline */}
        <line x1="0" y1={H} x2={W} y2={H} stroke="hsl(var(--line))" strokeWidth="0.15" vectorEffect="non-scaling-stroke" />
        {buckets.map((b, i) => {
          const x = i * slot + (slot - barW) / 2;
          const contactH = (b.contact / maxTotal) * (H - 1);
          const auditH   = (b["site-audit"] / maxTotal) * (H - 1);
          const yContact = H - contactH;
          const yAudit   = yContact - auditH;
          return (
            <g key={b.date}>
              {b.contact > 0 && (
                <rect
                  x={x} y={yContact} width={barW} height={contactH}
                  fill={SOURCE_COLOR.contact}
                >
                  <title>{`${formatDay(b.date)} · ${b.contact} contact${b.contact === 1 ? "" : "s"}`}</title>
                </rect>
              )}
              {b["site-audit"] > 0 && (
                <rect
                  x={x} y={yAudit} width={barW} height={auditH}
                  fill={SOURCE_COLOR["site-audit"]}
                >
                  <title>{`${formatDay(b.date)} · ${b["site-audit"]} audit${b["site-audit"] === 1 ? "" : "s"}`}</title>
                </rect>
              )}
            </g>
          );
        })}
      </svg>
      <div className="mt-3 flex justify-between font-ui text-[10px] tracking-widest uppercase text-label">
        <span>{formatDay(buckets[0].date)}</span>
        <span>{formatDay(buckets[Math.floor(buckets.length / 2)].date)}</span>
        <span>{formatDay(buckets[buckets.length - 1].date)}</span>
      </div>
    </div>
  );
}

// ---------- Page ----------
export default function AdminLeads() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [source, setSource] = useState("all");
  const [days, setDays] = useState(30);
  const [tier, setTier] = useState("all");
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        adminApi.get("/admin/leads", { params: { source, days, limit: 500 } }),
        adminApi.get("/admin/leads/stats", { params: { days } }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Could not load leads.");
    } finally {
      setRefreshing(false);
    }
  }, [source, days, logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(
    () => stats?.totals || { all: 0, contact: 0, "site-audit": 0 },
    [stats]
  );
  const conversionShare = useMemo(() => {
    if (!totals.all) return { contact: 0, audit: 0 };
    return {
      contact: Math.round((totals.contact / totals.all) * 100),
      audit:   Math.round((totals["site-audit"] / totals.all) * 100),
    };
  }, [totals]);

  const shown = useMemo(
    () => (leads && tier !== "all" ? leads.filter((l) => l.lead_tier === tier) : leads),
    [leads, tier]
  );

  const onExportCsv = () => {
    if (!shown?.length) return;
    const cols = [
      "created_at", "source", "lead_tier", "lead_score", "name", "email", "company", "budget",
      "location", "service", "timeline", "industry", "page_path",
      "audited_url", "utm_source", "utm_medium", "utm_campaign", "utm_term",
      "utm_content", "gclid", "message",
    ];
    const rows = [cols];
    for (const l of shown) rows.push(cols.map((c) => l[c] ?? ""));
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `redkey-leads-${source}-${days}d-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-leads-page">
      <PageHead
        title="Leads — Redkey Admin"
        description="Inbound leads dashboard."
      />

      {/* Header */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-20 pb-8">
        <Link
          to="/admin/journal"
          className="eyebrow link-underline-grow inline-flex items-center gap-2"
          data-testid="admin-leads-back"
        >
          <ArrowLeft size={12} aria-hidden />
          Back to blog manager
        </Link>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">§ Admin · Leads</p>
            <h1 className="font-display text-4xl md:text-6xl mt-4">
              <em className="not-italic text-brand-red">Inbound.</em>
            </h1>
            <p className="mt-4 font-body text-ink-soft">
              Every inbound lead — contact form and free audit — in one place.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              data-testid="admin-leads-refresh"
              className="inline-flex items-center gap-2 border hairline-strong px-5 py-3 hover:border-ink font-ui disabled:opacity-60"
            >
              <RefreshCcw size={14} aria-hidden />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={onExportCsv}
              disabled={!shown || shown.length === 0}
              data-testid="admin-leads-export"
              className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-3 hover:bg-electric transition-colors disabled:opacity-60 font-ui"
            >
              <Download size={14} aria-hidden />
              Export CSV
            </button>
          </div>
        </div>
      </section>

      {/* Stat cards + chart */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 border-t hairline pt-10 pb-8">
        {error && (
          <div className="border hairline-strong bg-paper-alt p-6 mb-6" data-testid="admin-leads-error">
            <p className="font-body text-ink">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-3 lg:col-span-1 lg:grid-cols-1 content-start">
            <div className="border hairline-strong p-5" data-testid="stat-total">
              <p className="font-ui text-[10px] tracking-widest uppercase text-label">Total · last {days}d</p>
              <p className="font-display text-4xl md:text-5xl mt-2 text-ink">{totals.all}</p>
            </div>
            <div className="border hairline-strong p-5" data-testid="stat-contact">
              <p className="font-ui text-[10px] tracking-widest uppercase text-label">Contact · {conversionShare.contact}%</p>
              <p className="font-display text-4xl md:text-5xl mt-2 text-ink">{totals.contact}</p>
            </div>
            <div className="border hairline-strong p-5" data-testid="stat-audit">
              <p className="font-ui text-[10px] tracking-widest uppercase text-label">Audit · {conversionShare.audit}%</p>
              <p className="font-display text-4xl md:text-5xl mt-2 text-brand-red">{totals["site-audit"]}</p>
            </div>
          </div>

          {/* Chart */}
          <div className="border hairline-strong p-5 lg:col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <p className="font-ui text-[10px] tracking-widest uppercase text-label">Daily leads</p>
                <p className="font-display text-2xl md:text-3xl mt-1">
                  Last <em className="not-italic text-brand-red">{days} days.</em>
                </p>
              </div>
              <div className="flex items-center gap-4 font-ui text-xs">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-3 h-3" style={{ background: SOURCE_COLOR.contact }} aria-hidden />
                  Contact
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-3 h-3" style={{ background: SOURCE_COLOR["site-audit"] }} aria-hidden />
                  Site audit
                </span>
              </div>
            </div>
            {stats
              ? <LeadsChart stats={stats} />
              : <p className="font-body text-ink-soft">Loading chart…</p>}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-4 pb-4" data-testid="admin-leads-filters">
        <div className="flex flex-wrap gap-6 items-end border-t hairline pt-5">
          <div>
            <p className="font-ui text-[10px] tracking-widest uppercase text-label">Source</p>
            <div className="mt-2 flex gap-1">
              {SOURCES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSource(s.key)}
                  data-testid={`filter-source-${s.key}`}
                  aria-pressed={source === s.key}
                  className={`px-3 py-2 text-xs font-ui border transition-colors ${
                    source === s.key
                      ? "bg-ink text-paper border-ink"
                      : "bg-transparent text-ink border-[hsl(var(--line-strong))] hover:border-ink"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-ui text-[10px] tracking-widest uppercase text-label">Window</p>
            <div className="mt-2 flex gap-1">
              {WINDOWS.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => setDays(w.key)}
                  data-testid={`filter-window-${w.key}`}
                  aria-pressed={days === w.key}
                  className={`px-3 py-2 text-xs font-ui border transition-colors ${
                    days === w.key
                      ? "bg-ink text-paper border-ink"
                      : "bg-transparent text-ink border-[hsl(var(--line-strong))] hover:border-ink"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="font-ui text-[10px] tracking-widest uppercase text-label">Lead quality</p>
            <div className="mt-2 flex gap-1">
              {TIERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  data-testid={`filter-tier-${t}`}
                  aria-pressed={tier === t}
                  className={`px-3 py-2 text-xs font-ui border transition-colors capitalize ${
                    tier === t
                      ? "bg-ink text-paper border-ink"
                      : "bg-transparent text-ink border-[hsl(var(--line-strong))] hover:border-ink"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pb-24">
        {leads === null && !error && (
          <p className="font-body text-ink-soft mt-6">Loading leads…</p>
        )}

        {shown !== null && shown.length === 0 && (
          <div className="border hairline-strong bg-paper-alt p-8 mt-4" data-testid="admin-leads-empty">
            <div className="flex items-start gap-4">
              <div className="mt-1 h-9 w-9 rounded-full bg-paper flex items-center justify-center border hairline-strong">
                <MailX size={16} aria-hidden />
              </div>
              <div>
                <p className="eyebrow">§ No leads in this window</p>
                <h2 className="font-display text-3xl mt-3">
                  Nothing to see here — yet.
                </h2>
                <p className="mt-3 font-body text-ink-soft max-w-prose">
                  Change the source or widen the window to see more. When new
                  submissions land, they&rsquo;ll show up here in real time.
                </p>
              </div>
            </div>
          </div>
        )}

        {shown !== null && shown.length > 0 && (
          <ul
            className="mt-6 divide-y divide-[hsl(var(--line-strong))] border-y border-[hsl(var(--line-strong))]"
            data-testid="admin-leads-list"
          >
            {shown.map((l) => {
              const isAudit = l.source === "site-audit";
              return (
                <li
                  key={l.id}
                  data-testid={`admin-lead-${l.id}`}
                  className="py-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-start"
                >
                  <div className="md:col-span-2 flex items-center gap-2">
                    <span
                      className={`inline-block font-ui text-[10px] tracking-widest uppercase px-2 py-0.5 border ${
                        isAudit
                          ? "text-brand-red-ink border-brand-red-ink"
                          : "text-ink border-[hsl(var(--line-strong))]"
                      }`}
                    >
                      {isAudit ? <><FileText size={10} className="inline mr-1 -mt-0.5"/> Audit</>
                              : <><Mail size={10} className="inline mr-1 -mt-0.5"/> Contact</>}
                    </span>
                  </div>
                  <div className="md:col-span-4">
                    <p className="font-ui text-ink text-sm">{l.name || "—"}</p>
                    <p className="mt-0.5 font-body text-sm">
                      <a href={`mailto:${l.email}?subject=Re%3A%20Your%20Redkey%20inquiry`}
                         className="text-electric-ink hover:underline break-all">{l.email}</a>
                    </p>
                    {l.company && (
                      <p className="mt-1 font-ui text-xs text-label">{l.company}{l.budget ? ` · ${l.budget}` : ""}</p>
                    )}
                    {l.lead_tier && (
                      <p className="mt-1 font-ui text-xs text-label" data-testid={`admin-lead-qual-${l.id}`}>
                        <span className={`inline-block mr-2 px-2 py-0.5 text-[10px] tracking-widest uppercase border ${TIER_STYLE[l.lead_tier]}`}>
                          {l.lead_tier}
                        </span>
                        {[l.location, l.service, l.timeline, !l.company && l.budget, l.industry].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {isAudit && l.audited_url && (
                      <p className="mt-1 font-ui text-xs">
                        <a href={l.audited_url} target="_blank" rel="noopener"
                           className="text-label hover:text-ink inline-flex items-center gap-1 break-all">
                          {l.audited_url} <ExternalLink size={10} aria-hidden />
                        </a>
                      </p>
                    )}
                    {(l.utm_source || l.utm_campaign || l.gclid) && (
                      <p
                        className="mt-1 font-ui text-[10px] tracking-widest uppercase text-brand-red-ink"
                        data-testid={`admin-lead-utm-${l.id}`}
                        title={JSON.stringify({
                          utm_source: l.utm_source, utm_medium: l.utm_medium,
                          utm_campaign: l.utm_campaign, utm_term: l.utm_term,
                          utm_content: l.utm_content, gclid: l.gclid,
                        })}
                      >
                        {l.gclid ? "Ads · " : ""}
                        {[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-4">
                    <p className="font-body text-sm text-ink-soft leading-relaxed line-clamp-3 whitespace-pre-wrap">
                      {l.message}
                    </p>
                  </div>
                  <div className="md:col-span-2 md:text-right font-ui text-xs text-label">
                    {formatTs(l.created_at)}
                    <div className="mt-1 flex md:justify-end gap-2 text-[10px] uppercase tracking-widest">
                      <span className={l.notification_sent ? "text-electric-ink" : "text-label/70"}
                            title="Notification sent to you">
                        {l.notification_sent ? "✓ notified" : "· notif"}
                      </span>
                      <span className={l.autoresponse_sent ? "text-electric-ink" : "text-label/70"}
                            title="Auto-response sent to visitor">
                        {l.autoresponse_sent ? "✓ auto-reply" : "· auto"}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
